/**
 * Page-aware helpers for the AI Manager: human labels for the context chip,
 * per-page starter prompts, and the workbench deep-link builder.
 */
import type { ChatContext } from './types'

export type PageKind = 'dashboard' | 'explorer' | 'workbench' | 'policies' | 'insights' | 'agent-hub' | 'data-manager' | 'settings' | 'admin' | 'other'

export function pageKind(pathname: string): PageKind {
  if (pathname === '/' || pathname === '') return 'dashboard'
  if (pathname.startsWith('/invoices')) return 'explorer'
  if (pathname.startsWith('/workbench')) return 'workbench'
  if (pathname.startsWith('/ai/policies')) return 'policies'
  if (pathname.startsWith('/ai/insights')) return 'insights'
  if (pathname.startsWith('/agent-hub')) return 'agent-hub'
  if (pathname.startsWith('/data-manager')) return 'data-manager'
  if (pathname.startsWith('/settings')) return 'settings'
  if (pathname.startsWith('/admin')) return 'admin'
  return 'other'
}

/** "Ai › Something" for a route without a page name of its own. Only the routes PageKind doesn't cover get here. */
function otherPageLabel(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean)
  return segments.length ? segments.map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' › ') : 'App'
}

export function humanize(value: string): string {
  return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/** The words the chip needs in the reader's language; AIManager builds them from the message files. */
export interface ChipLabels {
  page: (kind: Exclude<PageKind, 'other'>) => string
  explorerTab: (tab: string) => string
  selected: (count: number) => string
  /** Translated workbench tab name for the English `aiLabel` the context carries; the raw value if unknown. */
  workbenchTab: (aiLabel: string) => string
  dataManagerTab: (code: string) => string
}

/**
 * "Workbench · INV00789 · Match Visualizer" — the parts the chip shows so the
 * user can see exactly what the copilot knows about their location.
 */
export function contextChipParts(ctx: ChatContext, labels: ChipLabels, invoiceLabel?: string | null): string[] {
  const kind = pageKind(ctx.page)
  const parts: string[] = [kind === 'other' ? otherPageLabel(ctx.page) : labels.page(kind)]
  if (kind === 'workbench') {
    if (ctx.invoice_id != null) parts.push(invoiceLabel || `#${ctx.invoice_id}`)
    if (ctx.tab) parts.push(labels.workbenchTab(ctx.tab))
  } else if (kind === 'explorer') {
    if (ctx.tab) parts.push(labels.explorerTab(ctx.tab))
    const ids = ctx.selection?.invoice_ids
    if (Array.isArray(ids) && ids.length) parts.push(labels.selected(ids.length))
  } else if (kind === 'policies' && ctx.highlight) {
    parts.push(ctx.highlight)
  } else if (kind === 'data-manager' && ctx.tab) {
    parts.push(labels.dataManagerTab(ctx.tab))
  }
  return parts
}

export interface StarterPrompt {
  label: string
  query: string
  icon: 'barChart' | 'fileText' | 'search' | 'checkCircle' | 'xCircle' | 'send' | 'mail' | 'download' | 'brain' | 'bot' | 'database' | 'lightbulb' | 'helpCircle' | 'info' | 'activity' | 'flag'
}

/** Per-page starter prompts (task spec §2). Shown in the empty state. */
export function startersFor(ctx: ChatContext): StarterPrompt[] {
  const kind = pageKind(ctx.page)
  switch (kind) {
    case 'dashboard':
      return [
        { icon: 'barChart', label: 'Summarize the KPIs', query: 'Summarize the current AP KPIs and what needs my attention.' },
        { icon: 'flag', label: 'What needs review?', query: 'How many invoices need review and what are the top exception reasons?' },
        { icon: 'fileText', label: 'Financial summary PDF', query: 'Generate a financial summary report for the last 30 days as a PDF.' },
        { icon: 'activity', label: 'Vendor health', query: 'Which vendors have the lowest match rate right now?' },
      ]
    case 'explorer':
      return [
        { icon: 'search', label: 'Count by tab', query: 'How many invoices are in each explorer tab?' },
        { icon: 'checkCircle', label: 'Bulk approve matched', query: 'Approve all invoices in the current tab that are ready for approval.' },
        { icon: 'download', label: 'Export this view', query: 'Export the invoices in the current tab as a PDF.' },
        { icon: 'flag', label: 'Oldest exceptions', query: 'Show me the oldest invoices that still need review.' },
      ]
    case 'workbench':
      return [
        { icon: 'info', label: 'Summarize this invoice', query: 'Summarize this invoice.' },
        { icon: 'helpCircle', label: 'Why does it need review?', query: 'Why does this invoice need review? Reference the policies involved.' },
        { icon: 'checkCircle', label: 'Approve', query: 'Approve this invoice.' },
        { icon: 'xCircle', label: 'Reject', query: 'Reject this invoice because the price variance exceeds tolerance.' },
        { icon: 'send', label: 'Send to QA', query: 'Send this invoice to QA approval.' },
        { icon: 'mail', label: 'Draft vendor email', query: 'Draft an email to the vendor about the issue on this invoice.' },
        { icon: 'download', label: 'Download invoice PDF', query: 'Generate a PDF summary of this invoice for download.' },
      ]
    case 'policies':
      return [
        { icon: 'brain', label: 'Explain this policy', query: ctx.highlight ? `Explain policy ${ctx.highlight} and when it fires.` : 'Explain the active matching policies and when each fires.' },
        { icon: 'search', label: 'Which policy flags duplicates?', query: 'Which policy flags duplicate invoices? Highlight it for me.' },
        { icon: 'lightbulb', label: 'Create a policy', query: 'Help me create a new AP policy.' },
        { icon: 'activity', label: 'Most-fired policies', query: 'Which policies fire most often and on which vendors?' },
      ]
    case 'agent-hub':
      return [
        { icon: 'bot', label: 'Which agents are active?', query: 'Which orchestrators and operators are active right now?' },
        { icon: 'activity', label: 'Operator performance', query: 'Which operators have the lowest success rate?' },
        { icon: 'info', label: 'Explain this page', query: 'Explain what the Agent Hub shows.' },
      ]
    case 'data-manager':
      return [
        { icon: 'database', label: 'Last job status', query: 'What is the status of the last ingestion job?' },
        { icon: 'flag', label: 'Failed ingestions', query: 'Were there any failed ingestions recently, and why?' },
        { icon: 'info', label: 'Explain this page', query: 'Explain what I can do in the Data Manager.' },
      ]
    case 'insights':
      return [
        { icon: 'lightbulb', label: 'Top insights', query: 'What are the most important open insights?' },
        { icon: 'activity', label: 'Recurring patterns', query: 'What recurring exception patterns has the AI learned?' },
      ]
    default:
      return [
        { icon: 'helpCircle', label: 'What can you help me with?', query: 'What can you help me with?' },
        { icon: 'flag', label: 'What needs review?', query: 'How many invoices need review right now?' },
        { icon: 'barChart', label: 'KPI summary', query: 'Summarize the current AP KPIs.' },
      ]
  }
}

/** Map an invoice status to the explorer tab the workbench should page through. */
export function queueForStatus(status?: string | null): string {
  switch (status) {
    case 'needs_review':
    case 'rejected':
    case 'matched':
    case 'posted':
      return status
    case 'pending_payment':
    case 'paid':
      return 'payments'
    default:
      return status ? `status:${status}` : 'all'
  }
}

export function workbenchLink(invoiceDbId: number, queue?: string | null, status?: string | null): string {
  const q = queue || queueForStatus(status)
  return `/workbench?invoice=${invoiceDbId}&queue=${encodeURIComponent(q)}`
}
